import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";
import { DailyStock } from "@/api/daily-stock-api";

interface VariantStockInfoProps {
  stock?: DailyStock;
  quantity?: number;
  className?: string;
}

export function VariantStockInfo({
  stock,
  className = "",
}: VariantStockInfoProps) {
  const currentStock = stock?.stockInHand ?? 0;
  const soldToday = stock?.soldQty ?? 0;
  const available = stock?.closingQty ?? 0;

  // Determine stock status
  const getStockStatus = () => {
    if (available <= 0) return { label: "Out of Stock", color: "destructive" };
    if (available <= 5) return { label: "Low Stock", color: "secondary" };
    return { label: "In Stock", color: "default" };
  };

  const stockStatus = getStockStatus();

  return (
    <div className={`space-y-3 ${className}`}>
      <Badge variant={stockStatus.color as any}>{stockStatus.label}</Badge>

      {/* Main Stock Numbers */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">{currentStock}</div>
          <div className="text-xs text-muted-foreground">Opening</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{soldToday}</div>
          <div className="text-xs text-muted-foreground">Sold Today</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{available}</div>
          <div className="text-xs text-muted-foreground">Available</div>
        </div>
      </div>

      {/* Detailed Stock Movements (only if stock data exists) */}
      {stock && (
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Today's Movements
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between">
                <span>Opening:</span>
                <span className="font-medium">{stock.openQty}</span>
              </div>
              {stock.receiveQty > 0 && (
                <div className="flex items-center justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Received:
                  </span>
                  <span className="font-medium">+{stock.receiveQty}</span>
                </div>
              )}
              {stock.adjustInQty > 0 && (
                <div className="flex items-center justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Adjust In:
                  </span>
                  <span className="font-medium">+{stock.adjustInQty}</span>
                </div>
              )}
              {stock.adjustOutQty > 0 && (
                <div className="flex items-center justify-between text-red-600">
                  <span className="flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />
                    Adjust Out:
                  </span>
                  <span className="font-medium">-{stock.adjustOutQty}</span>
                </div>
              )}
            </div>
            <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
              Last updated: {stock.stockDate}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
